import * as React from "react";
import {useMemo, useReducer} from "react";

import type {Action, State} from "./content-cache-types.ts";
import reducer, {initialState} from "./reducer.ts";
import useHydratedContentState from "./use-hydrated-content-state.ts";

export type ContentCacheContextType = {
    state: State;
    dispatch: (action: Action) => void;
};

export const ListedContentCacheContext: React.Context<ContentCacheContextType> =
    React.createContext<ContentCacheContextType>({
        state: initialState,
        dispatch: (action: Action) => {},
    });
ListedContentCacheContext.displayName = "ListedContentCacheContext";

export const UnlistedContentCacheContext: React.Context<ContentCacheContextType> =
    React.createContext<ContentCacheContextType>({
        state: initialState,
        dispatch: (action: Action) => {},
    });
UnlistedContentCacheContext.displayName = "UnlistedContentCacheContext";

type Props = {
    children: React.ReactNode;
    initialState: State | null | undefined;
};

/**
 * ListedContentCache provides its children access to the *listed* content
 * cache state and a dispatcher via useContentCache.
 */
const ListedContentCache = (props: Props): React.ReactElement => {
    const [state, dispatch] = useReducer(
        reducer,
        props.initialState ?? initialState,
    );
    const cacheContext = useMemo(() => ({state, dispatch}), [state, dispatch]);

    return (
        <ListedContentCacheContext.Provider value={cacheContext}>
            {props.children}
        </ListedContentCacheContext.Provider>
    );
};

/**
 * UnlistedContentCache provides its children access to the *unlisted* content
 * cache state and a dispatcher via useContentCache. This is a clone of
 * ListedContentCache but with a different context provider.
 */
const UnlistedContentCache = (props: Props): React.ReactElement => {
    const [state, dispatch] = useReducer(
        reducer,
        props.initialState ?? initialState,
    );
    const cacheContext = useMemo(() => ({state, dispatch}), [state, dispatch]);

    return (
        <UnlistedContentCacheContext.Provider value={cacheContext}>
            {props.children}
        </UnlistedContentCacheContext.Provider>
    );
};

type CacheProps = {
    children: React.ReactNode;
    listedInitialState?: State;
    unlistedInitialState?: State;
};

/**
 * ContentCache provides its children access to both the listed content
 * cache and unlisted content cache states and a dispatchers via
 * useContentCache.
 */
export const ContentCache = (props: CacheProps): React.ReactElement => {
    const hydratedState = useHydratedContentState();

    // Initialize our caches with a provided initial state (useful for testing)
    // or state hydrated from SSR (if available).
    // We can't dispatch these things later because dispatch requires a second
    // render for the data to be available. That makes testing awkward and SSR
    // not function correctly. The first client side render after SSR needs to
    // match the final server-side SSR result exactly.
    return (
        <ListedContentCache
            initialState={
                props.listedInitialState ?? hydratedState?.listedState
            }
        >
            <UnlistedContentCache
                initialState={
                    props.unlistedInitialState ?? hydratedState?.unlistedState
                }
            >
                {props.children}
            </UnlistedContentCache>
        </ListedContentCache>
    );
};
